/**
 * The perk table (04 §4.2). Effects are data: multipliers, additions and flags on `SimMods`.
 * Every perk maps onto at least one field. A multiplier of 0 is how "never" is said: the
 * neutral value of `lawDeviation` and `wadeFatigue` is 1, so x0 sets them to none at all.
 */

import { TRACKS } from '../../sim/hazards/tracks';
import { RANGER, STORM } from '../../sim/tuning';
import type { Biome, SimMods } from '../../sim/types';
import type { PerkId } from '../types';

export type NumericModKey = { [K in keyof SimMods]: SimMods[K] extends number ? K : never }[keyof SimMods];
export type FlagModKey = { [K in keyof SimMods]: SimMods[K] extends boolean ? K : never }[keyof SimMods];

export interface ModEffect {
  mul?: Partial<Record<NumericModKey, number>>;
  add?: Partial<Record<NumericModKey, number>>;
  flags?: FlagModKey[];
  /** Cancels the weather/heat stamina penalty in this biome (applied as a matching stamina-pool bonus). */
  weatherProof?: Biome;
}

export type PerkFamily = 'marksman' | 'demolitions' | 'fieldcraft' | 'survival';

export interface PerkInfo {
  id: PerkId;
  family: PerkFamily;
  name: string;
  text: string;
  effect: ModEffect;
}

/** Seconds of bleed-out the sim starts from (02 §5); Hard to Kill adds 30 s to it. */
const BLEED_OUT_SECONDS = 45;

export const PERKS: readonly PerkInfo[] = [
  { id: 'steady-hands', family: 'marksman', name: 'Steady Hands', text: '-25 % aim spread while stationary.',
    effect: { mul: { aimSpreadStill: 0.75 } } },
  // R5: it zeroes ALL spread on those rounds, not just recoil - wounds, nerves and gait included (ROUND5 §3.7).
  { id: 'controlled-pairs', family: 'marksman', name: 'Controlled Pairs',
    text: 'The first 2 rounds of a burst go exactly where you point.', effect: { add: { burstFreeRounds: 2 } } },
  /*
   * R6, and deliberately left: a cooldown can only be whole ticks, so `round(108 x 0.7)` is 76 and
   * the reload is -29.6 %, not -30 %. Hothead's carry (firearms.ts) cannot help here - a reload
   * happens once, there is no next one to pay the fraction into - and the alternative is to bend
   * `CARBINE.reloadTicks` away from its 1.8 s so one perk's decimal comes out round. Four tenths of
   * a percent is under a single tick at 60 Hz; `round6Perks.test.ts` holds the error there.
   */
  { id: 'quick-reload', family: 'marksman', name: 'Quick Reload', text: '-30 % reload time.',
    effect: { mul: { reloadTime: 0.7 } } },
  { id: 'bunker-buster', family: 'marksman', name: 'Bunker Buster',
    text: 'LAW rockets never deviate; +1 LAW capacity.', effect: { mul: { lawDeviation: 0 }, add: { extraLaws: 1 } } },
  /** Same sub-tick rounding as Quick Reload: `round(18 x 0.6)` is 11, so -38.9 % of one fifth of a second. */
  { id: 'snap-shot', family: 'marksman', name: 'Snap Shot', text: '-40 % weapon-raise time after sprinting.',
    effect: { mul: { weaponRaiseTime: 0.6 } } },
  { id: 'deadeye', family: 'marksman', name: 'Deadeye', text: '+20 % effective carbine range.',
    effect: { mul: { carbineRange: 1.2 } } },

  // R5: nothing in the game can re-set a planted timer - not this perk, not anything (ROUND5 §3.10).
  { id: 'sapper', family: 'demolitions', name: 'Sapper',
    text: 'Plant and arm time bombs 40 % faster.',
    effect: { mul: { bombPlaceTime: 0.6 } } },
  { id: 'long-arm', family: 'demolitions', name: 'Long Arm', text: '+25 % grenade throw range.',
    effect: { mul: { grenadeRange: 1.25 } } },
  { id: 'cook-off', family: 'demolitions', name: 'Cook-off',
    text: 'Hold a grenade to shorten its fuse on release.', effect: { flags: ['cookOff'] } },
  { id: 'mine-sense', family: 'demolitions', name: 'Mine Sense',
    text: 'Mines within 4 m are revealed (3 m while running).', effect: { add: { mineSenseRadius: 4 } } },
  { id: 'wire-cutter', family: 'demolitions', name: 'Wire Cutter',
    text: 'Cross barbed wire at walk speed without noise or wounds.', effect: { flags: ['wireImmune'] } },
  // R6: the pouches come before the drag (`actions.ts`), so hauling a man you have just searched
  // costs a second press. That is the interaction, and a card that does not say so is a card the
  // player has to discover by being surprised.
  { id: 'salvager', family: 'demolitions', name: 'Salvager',
    text: 'Take one grenade or one magazine from a fallen enemy; Interact again to move him.',
    effect: { flags: ['salvager'] } },

  // R5: it never touched anyone's vision RANGE; it slows the detection meter while he is flat (ROUND5 §3.4).
  { id: 'ghost', family: 'fieldcraft', name: 'Ghost', text: 'Crawling, enemies notice you 20 % more slowly.',
    effect: { mul: { crawlDetection: 0.8 } } },
  { id: 'light-step', family: 'fieldcraft', name: 'Light Step',
    // R5: the footprint half is real now (TRACKS). It carries its own multiplier rather than
    // riding on `walkNoise`, so a future perk that only quietens a walk cannot inherit it.
    text: 'Walk noise radius -40 %; snow footprints fade 50 % faster.',
    effect: { mul: { walkNoise: 0.6, footprintFade: TRACKS.lightStepFade } } },
  // R5: "from the front" undersold it - the flag silences a kill on any man who knows you are
  // there, including one already shooting at you (ROUND5 §3.8).
  { id: 'knife-work', family: 'fieldcraft', name: 'Knife Work',
    text: 'Knife lunge range +0.5 m; a knife kill is silent even on a man who has seen you.',
    effect: { add: { knifeReachBonus: 0.5 }, flags: ['silentFrontalKnife'] } },
  { id: 'pathfinder', family: 'fieldcraft', name: 'Pathfinder',
    text: 'Parachute steering +30 %; pods you drop drift 50 % less.',
    effect: { mul: { parafoilSteering: 1.3, podDrift: 0.5 } } },
  { id: 'night-eyes', family: 'fieldcraft', name: 'Night Eyes', text: 'Your own vision penalty at night is halved.',
    effect: { mul: { nightPenalty: 0.5 } } },
  { id: 'impersonator', family: 'fieldcraft', name: 'Impersonator',
    text: 'In an enemy uniform, officers and elites grow suspicious 50 % slower.',
    effect: { mul: { disguiseScrutiny: 0.5 } } },

  // R6: there is no pool. The bar is a normalised 0-1 meter and `staminaPool` has one reader,
  // inside `if (r.running)`: it does not slow the wading drain and it does not speed recovery.
  // Making it a real pool would have to divide recovery by it too, which would hand Iron Lungs a
  // hidden penalty and Old Wound a hidden bonus - so the words came to the field (movement.ts).
  { id: 'iron-lungs', family: 'survival', name: 'Iron Lungs', text: 'He can run 25 % longer before he is winded.',
    effect: { mul: { staminaPool: 1.25 } } },
  { id: 'second-wind', family: 'survival', name: 'Second Wind',
    text: 'Stamina recovers 40 % faster while prone or in a trench.', effect: { mul: { staminaRecovery: 1.4 } } },
  { id: 'field-medic', family: 'survival', name: 'Field Medic',
    text: 'First-aid kits heal one extra wound step and apply 30 % faster.',
    effect: { add: { extraFirstAidHeal: 1 }, mul: { firstAidTime: 0.7 } } },
  { id: 'hard-to-kill', family: 'survival', name: 'Hard to Kill', text: 'Downed bleed-out timer +30 s.',
    effect: { mul: { bleedOutTime: (BLEED_OUT_SECONDS + 30) / BLEED_OUT_SECONDS } } },
  { id: 'pack-mule', family: 'survival', name: 'Pack Mule', text: '+4 carry units on the ranger (not in pods).',
    effect: { add: { carryBonus: 4 } } },
  { id: 'cold-blooded', family: 'survival', name: 'Cold Blooded', text: 'No weather stamina penalty in the arctic.',
    effect: { weatherProof: 'arctic' } },
  { id: 'desert-rat', family: 'survival', name: 'Desert Rat', text: 'No heat or weather stamina penalty in the desert.',
    effect: { weatherProof: 'desert' } },
  { id: 'woodsman', family: 'survival', name: 'Woodsman', text: 'No weather stamina penalty in temperate country.',
    effect: { weatherProof: 'temperate' } },
];

export const PERK_FAMILIES: readonly PerkFamily[] = ['marksman', 'demolitions', 'fieldcraft', 'survival'];

/** Ranks (index into RANKS) whose promotion offers a perk: CPL, SGT, PSG, 2LT, CPT, LTC (02 §11). */
export const PERK_RANKS: readonly number[] = [1, 2, 4, 6, 8, 10];

/** How many perks each promotion offers. */
export const PERK_CHOICES = 3;

/**
 * Stamina penalties a weather-proof perk cancels: desert heat +30 % fatigue, storms +20 % drain (02 §5).
 * Taken from the simulation's own numbers, not copied: these are paid as a stamina-pool bonus that has
 * to cancel the penalty EXACTLY, and two hand-kept copies of 0.3 would sooner or later stop matching.
 */
export const WEATHER_PENALTY = { desertHeat: RANGER.desertFatiguePenalty, storm: STORM.fatigue } as const;
